/**
 * Integration Marketplace Demo — canonical product dataset.
 *
 * THROWAWAY DEMO CODE (see ./README.md). One shared set of "real" products
 * (electrical-distribution domain) that every integration exposes through its
 * own raw column naming. Whichever source you connect, these are the products
 * that flow into Gently — only the raw source record differs. The catalog and
 * the item-master payoff join on array index, so keep this array stable.
 */

export interface CanonicalProduct {
  sku: string;
  name: string;
  description: string;
  category: string;
  manufacturer: string;
  uom: string;
  listPrice: number;
  cost: number;
  onHand: number;
  upc: string;
  weightLbs: number;
  /** Legacy/cross-reference part number used by some source systems. */
  legacyPart: string;
  createdOn: string;
}

export const CANONICAL_PRODUCTS: CanonicalProduct[] = [
  {
    sku: '12-2WG-250',
    name: '12/2 NM-B Wire w/ Ground, 250 ft',
    description:
      'Romex SIMpull 12/2 non-metallic sheathed cable, copper, 250 ft coil',
    category: 'Wire & Cable',
    manufacturer: 'Southwire',
    uom: 'ROLL',
    listPrice: 89.5,
    cost: 61.2,
    onHand: 340,
    upc: '032886791245',
    weightLbs: 18.5,
    legacyPart: 'SW-28828222',
    createdOn: '2019-03-14',
  },
  {
    sku: 'QO120',
    name: 'QO 20A Single-Pole Circuit Breaker',
    description:
      'Square D QO 20-Amp 1-Pole plug-on circuit breaker, 120/240 VAC',
    category: 'Circuit Protection',
    manufacturer: 'Square D',
    uom: 'EA',
    listPrice: 12.75,
    cost: 7.1,
    onHand: 1240,
    upc: '785901029830',
    weightLbs: 0.3,
    legacyPart: 'SQD-QO120',
    createdOn: '2017-08-02',
  },
  {
    sku: 'EMT-1/2-10',
    name: '1/2 in EMT Conduit, 10 ft',
    description: 'Electrical metallic tubing, galvanized steel, 1/2 in x 10 ft',
    category: 'Conduit & Raceway',
    manufacturer: 'Wheatland',
    uom: 'EA',
    listPrice: 6.2,
    cost: 3.85,
    onHand: 880,
    upc: '784825010109',
    weightLbs: 2.1,
    legacyPart: 'WHT-101243',
    createdOn: '2016-01-20',
  },
  {
    sku: 'LED-HB-150W',
    name: '150W LED High Bay Fixture, 5000K',
    description:
      'Lithonia 150-Watt LED high bay, 21000 lm, 5000K, 0-10V dimmable',
    category: 'Lighting',
    manufacturer: 'Lithonia',
    uom: 'EA',
    listPrice: 142.0,
    cost: 96.4,
    onHand: 215,
    upc: '745975410552',
    weightLbs: 11.2,
    legacyPart: 'LIT-IBG-15000',
    createdOn: '2021-05-11',
  },
  {
    sku: 'PVC-3/4-BELL',
    name: '3/4 in PVC Belled-End Conduit, Sch 40',
    description:
      'Schedule 40 PVC electrical conduit, belled end, 3/4 in x 10 ft',
    category: 'Conduit & Raceway',
    manufacturer: 'Cantex',
    uom: 'EA',
    listPrice: 3.1,
    cost: 1.75,
    onHand: 1500,
    upc: '724924050062',
    weightLbs: 1.0,
    legacyPart: 'CTX-A52DF',
    createdOn: '2015-11-03',
  },
  {
    sku: 'THHN-12-BLK',
    name: '12 AWG THHN Stranded Wire, Black, 500 ft',
    description:
      'THHN/THWN-2 12 AWG stranded copper building wire, black, 500 ft',
    category: 'Wire & Cable',
    manufacturer: 'Southwire',
    uom: 'ROLL',
    listPrice: 74.3,
    cost: 49.9,
    onHand: 410,
    upc: '032886118203',
    weightLbs: 16.0,
    legacyPart: 'SW-22968201',
    createdOn: '2018-07-19',
  },
  {
    sku: '4SQ-BOX',
    name: '4 in Square Steel Box, 2-1/8 Deep',
    description: 'Drawn 4 in square outlet box, 2-1/8 in deep, 30.3 cu in, KO',
    category: 'Boxes & Enclosures',
    manufacturer: 'RACO',
    uom: 'EA',
    listPrice: 2.45,
    cost: 1.2,
    onHand: 2600,
    upc: '050169002323',
    weightLbs: 0.6,
    legacyPart: 'RAC-232',
    createdOn: '2014-02-27',
  },
  {
    sku: 'GFCI-20A-WH',
    name: '20A GFCI Receptacle, White, Self-Test',
    description: 'Leviton SmartlockPro 20-Amp self-test GFCI receptacle, white',
    category: 'Wiring Devices',
    manufacturer: 'Leviton',
    uom: 'EA',
    listPrice: 18.9,
    cost: 11.4,
    onHand: 720,
    upc: '078477814116',
    weightLbs: 0.4,
    legacyPart: 'LEV-GFNT2-W',
    createdOn: '2020-09-30',
  },
  // ── "New" SKUs (indices 8–10): NOT in Gently's baseline catalog. Connected
  //    sources expose these so the demo shows brand-new item inserts. ──
  {
    sku: 'SPD-T2-50KA',
    name: 'Type 2 Surge Protective Device, 50kA',
    description: 'Eaton CHSPT2ULTRA Type 2 whole-home SPD, 50kA per phase',
    category: 'Surge Protection',
    manufacturer: 'Eaton',
    uom: 'EA',
    listPrice: 245.0,
    cost: 168.0,
    onHand: 90,
    upc: '786676183520',
    weightLbs: 1.4,
    legacyPart: 'EAT-CHSPT2ULTRA',
    createdOn: '2024-01-15',
  },
  {
    sku: 'CBL-CAT6-1000',
    name: 'Cat6 UTP Cable, 1000 ft Box, Blue',
    description:
      'CommScope Cat6 U/UTP riser-rated cable, 23 AWG, 1000 ft box, blue',
    category: 'Datacomm',
    manufacturer: 'CommScope',
    uom: 'BOX',
    listPrice: 189.0,
    cost: 121.5,
    onHand: 260,
    upc: '732559331004',
    weightLbs: 31.0,
    legacyPart: 'CS-7131704',
    createdOn: '2023-09-08',
  },
  {
    sku: 'DISC-60A-3R',
    name: '60A Non-Fused Disconnect, NEMA 3R',
    description:
      'Siemens 60-Amp 240V non-fused general-duty safety switch, NEMA 3R',
    category: 'Safety Switches',
    manufacturer: 'Siemens',
    uom: 'EA',
    listPrice: 88.5,
    cost: 57.2,
    onHand: 140,
    upc: '783643148802',
    weightLbs: 6.8,
    legacyPart: 'SIE-GNF323R',
    createdOn: '2024-03-22',
  },
];

/** SKUs already in Gently's baseline catalog (the AS/400-connected set). */
export const CORE_PRODUCT_COUNT = 8;

/** Items in Gently's baseline catalog (the IBM i / AS/400 source of record). */
export const CORE_PRODUCTS = CANONICAL_PRODUCTS.slice(0, CORE_PRODUCT_COUNT);

/**
 * Per-source field discrepancies applied to a connected (non-baseline) source's
 * products, so matching against the baseline surfaces sanity-check warnings.
 * Keyed by SKU; only the listed fields differ from the canonical truth.
 */
export const SOURCE_DISCREPANCIES: Record<
  string,
  Partial<
    Pick<CanonicalProduct, 'listPrice' | 'cost' | 'manufacturer' | 'onHand'>
  >
> = {
  // Price drifted up in the source ERP vs. Gently's record.
  QO120: { listPrice: 13.4 },
  // Vendor recorded under the parent brand in the source ERP.
  'LED-HB-150W': { manufacturer: 'Acuity Brands' },
};

/** Apply the demo discrepancies to a product set (used for connected sources). */
export function withDiscrepancies(
  products: CanonicalProduct[],
): CanonicalProduct[] {
  return products.map((p) => {
    const override = SOURCE_DISCREPANCIES[p.sku];
    return override ? { ...p, ...override } : p;
  });
}
